/**
 * TORIARA NATURALS — Elite PDP System
 * pdp.js  v3.0  (MLV/Doubly currency fix — 25 Aug 2026)
 *
 * FIXES IN THIS VERSION:
 *  #A  Sticky price shows NGN instead of visitor's currency (e.g. USD)
 *      Root cause: syncStickyPrice() read [data-product-price] textContent
 *      before Doubly had converted it — always showed raw NGN.
 *  #B  Sticky price stale after variant switch
 *      Root cause: syncStickyPrice() ran immediately on variant:change,
 *      before Impulse + Doubly had time to write + convert the new price.
 *  #C  Sticky price not converted by Doubly
 *      Root cause: price was set as plain textContent, not span.money —
 *      Doubly skips anything that isn't span.money.
 *
 * SOLUTION:
 *  - On variant:change: read price from the variant object directly
 *    (integer in NGN subunits), format via theme.Currency.formatMoney,
 *    wrap in <span class="money">, then call DoublyCurrency.convertAll
 *    after a 120ms delay (allows Impulse to finish updating the DOM first).
 *  - On page load: copy innerHTML (not textContent) from [data-product-price]
 *    which already has the span.money wrapper Impulse renders, then call
 *    DoublyCurrency.convertAll after Doubly's own init delay (~600ms).
 *  - Currency switcher change: re-sync sticky price immediately.
 *
 * All other fixes from v2.0 are preserved unchanged.
 */
(function () {
  'use strict';

  function qs(sel, ctx)  { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ── Helper: call DoublyCurrency.convertAll safely ── */
  function triggerDoublyConvert() {
    if (window.DoublyCurrency && typeof DoublyCurrency.convertAll === 'function') {
      var sel = document.querySelector('[name="doubly-currencies"]');
      var cur = sel ? sel.value : (DoublyCurrency.currentCurrency || '');
      if (cur) DoublyCurrency.convertAll(cur);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     1. MOBILE STICKY ATC — MLV/Doubly currency-aware
  ═══════════════════════════════════════════════════════════════════════ */
  function initStickyATC() {
    var stickyBar    = qs('.pdp-sticky-atc');
    var stickyBtn    = qs('.pdp-sticky-atc__btn');
    var anchorBtn    = qs('[data-pdp-atc-anchor]');

    if (!stickyBar || !anchorBtn) return;

    var stickyPriceEl = qs('.pdp-sticky-atc__price', stickyBar);

    /* ── FIX #A + #C: Set price as span.money and trigger Doubly ──────
       We write a <span class="money"> so Doubly's convertAll() can find
       and convert it. We never use textContent (which strips the wrapper).
       delay: ms to wait before calling convertAll after writing price.    */
    function setStickyPrice(html, delay) {
      if (!stickyPriceEl) return;
      stickyPriceEl.innerHTML = html;
      setTimeout(triggerDoublyConvert, delay || 0);
    }

    /* ── FIX #B: variant:change — read from event.detail.variant ──────
       variant.price is the raw integer (e.g. 4154519 kobo = ₦41,545.19).
       theme.Currency.formatMoney converts it to the store money format string.
       We then wrap it in span.money so Doubly converts it to the visitor's
       currency. The 150ms delay lets Impulse finish its own DOM update first. */
    function syncFromVariant(variant) {
      if (!variant || !stickyPriceEl) return;

      var moneyFormat  = (window.theme && theme.settings && theme.settings.moneyFormat)
                         || '{{amount}}';
      var formatted    = (window.theme && theme.Currency && theme.Currency.formatMoney)
                         ? theme.Currency.formatMoney(variant.price, moneyFormat)
                         : variant.price;

      setStickyPrice('<span class="money">' + formatted + '</span>', 150);
    }

    /* ── Page load: copy innerHTML from main price element ─────────────
       Impulse already renders [data-product-price] with span.money wrappers.
       Copying innerHTML preserves those wrappers. Doubly converts after 650ms
       (enough time for Doubly's own XHR + init to complete). */
    function syncFromDOM() {
      var mainPrice = qs('[data-product-price]');
      if (mainPrice && stickyPriceEl) {
        var html = mainPrice.innerHTML.trim();
        if (html) setStickyPrice(html, 650);
      }
    }
    syncFromDOM();

    /* ── variant:change ─────────────────────────────────────────────── */
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      syncFromVariant(variant);
      syncStickyVariant(variant);
    });

    /* ── Re-sync when visitor switches Doubly currency ─────────────── */
    function onDoublyCurrencyChange() {
      /* Re-copy from main price (already converted by Doubly) */
      setTimeout(function() {
        var mainPrice = qs('[data-product-price]');
        if (mainPrice && stickyPriceEl) {
          stickyPriceEl.innerHTML = mainPrice.innerHTML.trim();
        }
      }, 200);
    }

    /* Watch for Doubly currency switcher (injected async) */
    function attachDoublySwitcher() {
      var sel = document.querySelector('[name="doubly-currencies"]');
      if (sel) {
        sel.addEventListener('change', onDoublyCurrencyChange);
        return true;
      }
      return false;
    }

    if (!attachDoublySwitcher() && 'MutationObserver' in window) {
      var mo = new MutationObserver(function(_, obs) {
        if (attachDoublySwitcher()) obs.disconnect();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    /* ── Disabled state + label from variant:change ─────────────────── */
    function syncStickyVariant(variant) {
      if (!stickyBtn || !variant) return;
      var available = variant.available;
      stickyBtn.disabled = !available;
      stickyBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
      stickyBtn.setAttribute('aria-label', available ? 'Add to cart' : 'Currently sold out');
    }

    /* ── IntersectionObserver — show/hide bar ─────────────────────── */
    function setBarVisibility(visible) {
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          setBarVisibility(!entry.isIntersecting);
        });
      }, { threshold: 0, rootMargin: '0px 0px -20px 0px' });
      observer.observe(anchorBtn);
    } else {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (!ticking) {
          requestAnimationFrame(function () {
            var rect = anchorBtn.getBoundingClientRect();
            setBarVisibility(rect.bottom < 0);
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }

    /* ── Sticky btn click ─────────────────────────────────────────── */
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var realBtn = qs('[data-add-to-cart][data-pdp-atc-anchor]');
        if (realBtn && !realBtn.disabled) realBtn.click();
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     2. ACCORDION SYSTEM (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initAccordions() {
    qsa('.pdp-accordion').forEach(function (panel) {
      var trigger = qs('.pdp-accordion__trigger', panel);
      var body    = qs('.pdp-accordion__body', panel);
      if (!trigger || !body) return;
      var bodyId = 'pdp-acc-body-' + Math.random().toString(36).slice(2, 8);
      body.id = bodyId;
      trigger.setAttribute('aria-controls', bodyId);
      var initiallyOpen = panel.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(initiallyOpen));
      body.setAttribute('aria-hidden', String(!initiallyOpen));
      trigger.addEventListener('click', function () {
        var isOpen = panel.getAttribute('aria-expanded') === 'true';
        panel.setAttribute('aria-expanded',  String(!isOpen));
        trigger.setAttribute('aria-expanded', String(!isOpen));
        body.setAttribute('aria-hidden',      String(isOpen));
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     3. ATC LOADING STATE (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initATCLoadingState() {
    var form = qs('.product-single__form');
    if (!form) return;
    function clearAllLoading() {
      qsa('[data-add-to-cart]').forEach(function (b) { b.classList.remove('loading'); });
    }
    form.addEventListener('submit', function () {
      var btn = qs('[data-add-to-cart]', form);
      if (btn) btn.classList.add('loading');
    });
    document.addEventListener('ajaxProduct:added', clearAllLoading);
    document.addEventListener('ajaxProduct:error', clearAllLoading);
    form.addEventListener('submit', function () { setTimeout(clearAllLoading, 3000); });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     4. SAVE BADGE (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initSaveBadge() {
    var badge = qs('.pdp-save-badge');
    if (!badge) return;
    function updateBadge(variant) {
      if (!variant) return;
      if (variant.compare_at_price > variant.price && variant.price > 0) {
        var pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        badge.textContent = 'Save ' + pct + '%';
        badge.setAttribute('aria-label', 'Save ' + pct + ' percent');
        badge.style.display = 'inline-flex';
      } else {
        badge.style.display = 'none';
      }
    }
    document.addEventListener('variant:change', function (e) {
      updateBadge(e.detail && e.detail.variant);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     5. URGENCY ROW (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initUrgencyRow() {
    var urgencyRow = qs('.pdp-urgency-row');
    if (!urgencyRow) return;
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      urgencyRow.style.display = variant.available ? '' : 'none';
      var textEl = qs('.pdp-urgency-text', urgencyRow);
      if (textEl && variant.available) {
        textEl.innerHTML = '<em>In Stock</em> — Order today, dispatched within 1–2 business days';
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     6. REVIEWS ANCHOR (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initReviewsAnchor() {
    qsa('[data-scroll-to-reviews]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var target = qs('#jdgm-widget') || qs('.jdgm-widget') || qs('[id*="judge"]') || qs('[class*="jdgm"]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     7. VARIANT LABEL SYNC (unchanged from v2.0)
  ═══════════════════════════════════════════════════════════════════════ */
  function initVariantLabelSync() {
    function syncLabels() {
      qsa('.pdp-option-label').forEach(function (labelEl) {
        var optionName = labelEl.getAttribute('data-option-name');
        if (!optionName) return;
        var safeName = optionName.replace(/\s+/g, '-').toLowerCase();
        var checked = qs('input[type="radio"][name="' + safeName + '"]:checked') ||
                      qs('input[type="radio"][name="' + optionName + '"]:checked');
        var valEl = qs('.pdp-option-selected-val', labelEl);
        if (checked && valEl) {
          valEl.textContent = '\u2014 ' + (checked.getAttribute('data-value') || checked.value);
        }
      });
    }
    syncLabels();
    document.addEventListener('change', function (e) {
      if (e.target && e.target.type === 'radio') syncLabels();
    });
  }

  /* ── Init ── */
  function init() {
    initStickyATC();
    initAccordions();
    initATCLoadingState();
    initSaveBadge();
    initUrgencyRow();
    initReviewsAnchor();
    initVariantLabelSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
